import React from "react";

import { Avatar, Box, Typography, useMediaQuery } from "@mui/material";
import { withStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import { theme } from "@/theme/customTheme";
import { humanizeDate } from "@/utils/datetime";
import { FragmentType } from "@/utils/type";

const Container = withStyles(Box, {
  root: {
    alignItems: "center",
    display: "flex",
    marginBlock: "20px",
    padding: "8px",
  },
});

const ClientAvatar = withStyles(Avatar, {
  root: {
    border: "2px solid #26C9A7",
    marginRight: "10px",
  },
});

const ClientNameText = withStyles(Typography, {
  root: {
    color: "#26C9A7",
    fontSize: 16,
    fontWeight: 400,
  },
});

const ClientProgramText = withStyles(Typography, {
  root: {
    color: "#4C515A",
    fontSize: 14,
    fontWeight: 600,
  },
});

const NotificationDateText = withStyles(Typography, {
  root: {
    color: "#4C515A",
    fontSize: 14,
    fontWeight: 600,
    width: "20%",
  },
});

const MobileDateText = withStyles(Typography, {
  root: {
    color: "#8A9099",
    fontSize: 12,
    fontWeight: 600,
    marginTop: "4px",
  },
});

const NotificationMessageText = withStyles(Typography, {
  root: {
    color: "#4C515A",
    fontSize: 14,
    fontWeight: 600,
  },
});

const notificationTileFragment = graphql(`
  fragment NotificationDetails on Notification {
    id
    notifier {
      ... on Client {
        id
        profile {
          id
          avatarUrl
          fullName
        }
        programForDate(date: $date) {
          id
          name
        }
      }
    }
    message
    createdAt
  }
`);

interface NotificationTileProps {
  notification: FragmentType<typeof notificationTileFragment>;
}

const NotificationTile = React.forwardRef<
  HTMLDivElement,
  NotificationTileProps
>(({ notification }, ref) => {
  const client =
    notification.notifier.__typename === "Client"
      ? notification.notifier
      : undefined;

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const avatar = (
    <ClientAvatar
      src={client?.profile?.avatarUrl ?? undefined}
      alt={`${client?.profile?.fullName} photo`}
    >
      {client?.profile?.fullName?.charAt(0)}
    </ClientAvatar>
  );

  if (isMobile) {
    return (
      <Container ref={ref} sx={{ alignItems: "flex-start" }}>
        {avatar}
        <Box minWidth={0} flex={1}>
          <ClientNameText>{client?.profile?.fullName}</ClientNameText>
          <ClientProgramText>{client?.programForDate?.name}</ClientProgramText>
          <NotificationMessageText sx={{ marginTop: "4px" }}>
            {notification.message}
          </NotificationMessageText>
          <MobileDateText>
            {humanizeDate(notification.createdAt)}
          </MobileDateText>
        </Box>
      </Container>
    );
  }

  return (
    <Container ref={ref}>
      {avatar}

      <Box width="22%">
        <ClientNameText>{client?.profile?.fullName}</ClientNameText>
        <ClientProgramText>{client?.programForDate?.name}</ClientProgramText>
      </Box>

      <NotificationDateText>
        {humanizeDate(notification.createdAt)}
      </NotificationDateText>

      <NotificationMessageText>{notification.message}</NotificationMessageText>
    </Container>
  );
});

export default NotificationTile;
